import React, { SetStateAction, useState } from 'react';

import classnames from 'classnames';
import { produce } from 'immer';
import { isEqual } from 'lodash';

import { ProfileErrorMessage } from 'pages/Settings/GeoConfigSettings';
import { PostGeoConfigLocation } from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import Message from '@lotlinx-aws/sparky-component-library/Sparky/Message';
import { COLOR } from '@lotlinx-aws/sparky-component-library/Sparky/styles/vars';
import { TrashCan } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/TrashCan';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import TextInputField from '@lotlinx-aws/sparky-component-library/Sparky/TextInputField';

import styles from './CreatNewPin.module.scss';
import SetPinDetail from '../SetPinDetail';

export interface CreatNewPinProps {
  editingPin?: PostGeoConfigLocation;
  setEditingPin: React.Dispatch<SetStateAction<PostGeoConfigLocation | undefined>>;
  isModifying: boolean;
  discardable?: boolean;
  errorMessage: ProfileErrorMessage;
  pinsLength: number;
  onLongChange(e: React.ChangeEvent<HTMLInputElement>): void;
  onLatChange(e: React.ChangeEvent<HTMLInputElement>): void;
  onRadiusChange(value: number[]): void;
  onDiscardPin?(): void;
  onAddPin?(): void;
  onSavePin?(e: React.MouseEvent): void;
  onCancelModifyPin?(): void;
  onDeletePin?(): void;
}

/**
 * CreatNewPin
 */
export default function CreatNewPin({
  editingPin,
  setEditingPin,
  isModifying,
  discardable,
  errorMessage,
  pinsLength,
  onLongChange,
  onLatChange,
  onRadiusChange,
  onDiscardPin,
  onAddPin,
  onSavePin,
  onCancelModifyPin,
  onDeletePin,
}: CreatNewPinProps) {
  const [existingPin] = useState<PostGeoConfigLocation | undefined>(editingPin);
  // check if pin detail is changed
  const isPinChanged = !isEqual(existingPin, editingPin);

  return (
    <div className={styles.newPin}>
      {isModifying && existingPin ? (
        <div className={styles.newPinHeader}>
          <Text color='secondary' size='16' fontWeight={4}>
            Edit {editingPin?.name}
          </Text>
          {pinsLength > 1 && (
            <IconButton aria-label='delete pin' size='sm' onClick={onDeletePin}>
              <TrashCan color={COLOR.SPARKY_RED_400} />
            </IconButton>
          )}
        </div>
      ) : (
        <Text color='secondary' size='16' fontWeight={4}>
          Add new pin to profile
        </Text>
      )}
      <div className={styles.newPinContent}>
        <TextInputField
          id='newPin'
          label='Pin label'
          value={existingPin?.name}
          onChange={(e) =>
            setEditingPin(
              produce(editingPin, (draft) => {
                if (draft) {
                  draft.name = e.target.value;
                }
                return draft;
              }),
            )
          }
          disabled
          className={styles.textInput}
        />
        <SetPinDetail
          editingPin={editingPin}
          onRadiusChange={onRadiusChange}
          onLongChange={onLongChange}
          onLatChange={onLatChange}
          errorMessage={errorMessage}
        />
      </div>
      <div className={styles.newPinFooter}>
        {pinsLength === 1 && !isModifying && errorMessage.atLeastOnePin && (
          <Message variant='muted' category='danger' dismissable={false} collapsible={false}>
            {errorMessage.atLeastOnePin}
          </Message>
        )}
        {isModifying ? (
          <>
            <Button
              variant='outline'
              size='sm'
              onClick={onCancelModifyPin}
              className={styles.cancelButton}
            >
              Cancel
            </Button>
            {isPinChanged && (
              <Button
                variant='solid'
                size='sm'
                disabled={
                  !!(
                    errorMessage.pinRadius ||
                    errorMessage.pinLatitude ||
                    errorMessage.pinLongitude
                  )
                }
                onClick={onSavePin}
                className={styles.applyChangeButton}
              >
                Apply changes
              </Button>
            )}
          </>
        ) : (
          <>
            {discardable && (
              <Button
                variant='outline'
                size='sm'
                onClick={onDiscardPin}
                className={styles.discardButton}
              >
                Discard
              </Button>
            )}
            <Button
              variant='solid'
              size='sm'
              onClick={onAddPin}
              disabled={
                !!(errorMessage.pinRadius || errorMessage.pinLatitude || errorMessage.pinLongitude)
              }
              className={classnames({ [styles.discardButton]: !discardable })}
            >
              Add pin
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
